import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCurrency } from "@/context/CurrencyContext";
import { ArrowLeft, X, Sparkles, MapPin, GraduationCap, Link2, Check, Printer } from "lucide-react";
import { toast } from "sonner";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  ResponsiveContainer, Tooltip, Legend,
} from "recharts";
import SEOHead from "@/components/app/SEOHead";
import ConfirmPillarPanel from "@/components/ConfirmPillarPanel";

// Match the 4-city palette used on the city detail radars so students
// see the same colour-code across pages. First city always takes the
// primary purple.
const RADAR_PALETTE = [
  { stroke: "#7C3AED", fill: "#7C3AED", opacity: 0.30 },
  { stroke: "#F59E0B", fill: "#F59E0B", opacity: 0.16 },
  { stroke: "#EC4899", fill: "#EC4899", opacity: 0.14 },
  { stroke: "#10B981", fill: "#10B981", opacity: 0.14 },
];
const CAREER_ORDER = [
  "research", "tech", "engineering", "healthcare", "finance", "law",
  "creative", "government", "startups", "fintech", "biotech",
  "aerospace", "cleantech", "gaming", "publishing", "content_creation",
  "agencies", "ecommerce", "mobility", "agritech", "edtech",
];
const CAREER_SHORT = {
  research: "Research", tech: "Software", engineering: "Engineering",
  healthcare: "Health", finance: "Finance", law: "Law",
  creative: "Creative", government: "Gov", startups: "Startups",
  fintech: "Fintech", biotech: "Biotech", aerospace: "Aerospace",
  cleantech: "Cleantech", gaming: "Gaming", ecommerce: "Ecom",
  mobility: "Mobility", agritech: "Agri", edtech: "EdTech",
  publishing: "Publish", content_creation: "Content", agencies: "Agencies",
};
const TRANSPORT_ORDER = ["rail", "air", "walk", "cycle", "night"];
const TRANSPORT_SHORT = {
  rail: "Rail / PT", air: "Air", walk: "Walk", cycle: "Cycle", night: "Night",
};
const LIFESTYLE_ORDER = [
  "arts", "museums", "live_music", "festivals", "cafe", "food",
  "sports_participate", "outdoors", "beach", "green",
  "nightlife", "sports_watch", "diverse",
];
const LIFESTYLE_SHORT = {
  arts: "Arts", museums: "Museums", food: "Food",
  green: "Green", nightlife: "Nightlife",
  sports_participate: "Sport (do)", sports_watch: "Sport (watch)",
  outdoors: "Outdoors", beach: "Beach", diverse: "Diverse",
  live_music: "Music", cafe: "Cafe", festivals: "Festivals",
};
// Climate radar — 4 preference axes from the WUA pack + a derived
// "Overall comfort" axis (100 − mean of the 5 risk scores). All axes
// share the 0-100 scale native to the climate CSV.
const CLIMATE_ORDER = ["warm_sunny", "mild", "four_seasons", "cool", "comfort"];
const CLIMATE_SHORT = {
  warm_sunny:  "Warm & sunny",
  mild:        "Mild",
  four_seasons:"Four seasons",
  cool:        "Cool",
  comfort:     "Overall comfort",
};

const currencySymbol = (c) => ({ GBP: "£", USD: "$" }[c] || "");

// Rows to compare — kept single-source-of-truth so easy to extend later.
const COST_ROWS = [
  ["Total monthly (typical)",    (c) => c.cost_of_living?.total_monthly,          true],
  ["Bare-bones student budget",  (c) => c.cost_of_living?.student_budget_low,     false],
  ["Student accommodation",       (c) => c.cost_of_living?.student_accommodation, false],
  ["Rent (1BR, centre)",          (c) => c.cost_of_living?.rent_1br_center,        false],
  ["Rent (1BR, outside)",         (c) => c.cost_of_living?.rent_1br_outside,       false],
  ["Utilities / month",           (c) => c.cost_of_living?.utilities_monthly,      false],
  ["Internet / month",            (c) => c.cost_of_living?.internet_monthly,       false],
  ["Mobile phone / month",        (c) => c.cost_of_living?.mobile_monthly,         false],
  ["Weekly shop",                 (c) => c.cost_of_living?.weekly_shop,            false],
  ["Groceries / month",           (c) => c.cost_of_living?.groceries_monthly,      false],
  ["Monthly transport",           (c) => c.cost_of_living?.monthly_transport,      false],
  ["Student gym / month",         (c) => c.cost_of_living?.gym_membership,         false],
  ["Pint of beer",                (c) => c.cost_of_living?.beer_pint,              false],
  ["Coffee",                      (c) => c.cost_of_living?.coffee,                  false],
  ["Cinema ticket",              (c) => c.cost_of_living?.cinema_ticket,           false],
  ["Sandwich",                   (c) => c.cost_of_living?.sandwich,                false],
  ["Cheap restaurant meal",     (c) => c.cost_of_living?.cheap_restaurant,        false],
  ["Laundry / load",             (c) => c.cost_of_living?.laundry_load,            false],
];

export default function CompareCities() {
  const [params, setParams] = useSearchParams();
  const slugs = (params.get("slugs") || "").split(",").filter(Boolean);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const { formatFrom, convertFrom, currency } = useCurrency();

  useEffect(() => {
    if (!slugs.length) { setCities([]); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get("/cities/compare", { params: { slugs: slugs.join(",") } });
        if (!cancelled) setCities(res.data?.cities || []);
      } catch {
        if (!cancelled) setCities([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.toString()]);

  const removeCity = (slug) => {
    const next = slugs.filter((s) => s !== slug);
    setParams(next.length ? { slugs: next.join(",") } : {}, { replace: true });
  };

  // Deep-link share: current URL always encodes the selected slugs, so
  // "copy" is just window.location. Toast confirms + falls back to a
  // manual selection if navigator.clipboard is unavailable.
  const [copied, setCopied] = useState(false);
  const handleCopyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied — paste it into a message or your shortlist");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Legacy fallback: create a hidden textarea, select, exec copy.
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success("Link copied"); } catch { toast.error("Copy failed — long-press the URL to copy manually"); }
      document.body.removeChild(ta);
    }
  };

  // Print → PDF: uses the browser's native Save-as-PDF dialog with a
  // scoped print stylesheet at the bottom of this file that hides chrome
  // (nav, footer, cookie banner) and keeps just the compare content.
  const handleDownloadPdf = () => {
    document.body.classList.add("compare-printing");
    setTimeout(() => {
      window.print();
      // Class removed on afterprint listener, mounted in the effect below.
    }, 60);
  };
  useEffect(() => {
    const clear = () => document.body.classList.remove("compare-printing");
    window.addEventListener("afterprint", clear);
    return () => window.removeEventListener("afterprint", clear);
  }, []);

  const bestFor = (rowGetter) => {
    if (cities.length < 2) return null;
    // Convert to display currency first so cross-currency comparison
    // is actually meaningful (GBP 950 vs USD 1300 must be normalised).
    const normalised = cities
      .map((c) => {
        const raw = rowGetter(c);
        if (raw == null) return null;
        return { city: c, raw, converted: convertFrom(raw, c.cost_of_living?.currency) };
      })
      .filter(Boolean);
    if (!normalised.length) return null;
    const minConverted = Math.min(...normalised.map((n) => n.converted));
    const winner = normalised.find((n) => n.converted === minConverted);
    return winner ? { slug: winner.city.slug, raw: winner.raw } : null;
  };

  return (
    <div className="min-h-screen bg-[#FBF8F3]">
      <SEOHead
        title="Compare student cities — WhichUniversityAdvisor"
        description="Side-by-side comparison of student cities: cost of living, neighbourhoods, and friendliness."
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 pb-6">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Link
            to="/cities/matcher"
            data-testid="compare-back-to-matcher"
            className="text-xs uppercase tracking-wider text-[#7C3AED] hover:text-[#5b21b6] inline-flex items-center gap-1.5 font-medium"
          >
            <ArrowLeft className="h-3 w-3" /> Back to City Matcher
          </Link>
          <Link
            to="/cities"
            data-testid="compare-back-link"
            className="text-xs uppercase tracking-wider text-stone-500 hover:text-[#002147] inline-flex items-center gap-1.5"
          >
            All city guides
          </Link>
        </div>
        <h1 className="font-editorial text-4xl sm:text-5xl mt-4 tracking-tight">
          Compare student cities
        </h1>
        <p className="mt-3 text-stone-600 max-w-2xl">
          Prices convert to your selected currency ({currency}). Green highlight = cheapest for that row.
        </p>
        {cities.length > 0 && (
          <div className="mt-5 flex flex-wrap items-center gap-2 no-print" data-testid="compare-toolbar">
            <button
              onClick={handleCopyLink}
              data-testid="compare-copy-link"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-stone-300 bg-white hover:border-[#7C3AED] hover:text-[#7C3AED] transition"
            >
              {copied
                ? <><Check className="h-3.5 w-3.5" /> Link copied</>
                : <><Link2 className="h-3.5 w-3.5" /> Copy share link</>}
            </button>
            <button
              onClick={handleDownloadPdf}
              data-testid="compare-download-pdf"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[#7C3AED] text-white hover:bg-[#6D28D9] transition"
            >
              <Printer className="h-3.5 w-3.5" /> Download PDF
            </button>
            <span className="text-[11px] text-stone-400 ml-1">
              PDF uses your browser's Save-as-PDF · one page, all four radars
            </span>
          </div>
        )}

        {/* Confirm-cities panel — the explicit gate that turns the
            PLACE chip green from the compare view too. Passes the
            fully-loaded city objects so the confirmed list carries
            slug + name + country into the journey profile. */}
        {cities.length > 0 && (
          <div className="mt-6">
            <ConfirmPillarPanel
              pillar="place"
              choices={cities.map((c) => ({
                slug: c.slug, name: c.name, country: c.country,
              }))}
              min={1}
              cap={4}
              renderItem={(c) => c.name || c.slug}
              nextTo="/admission"
              nextLabel="Next: check my chances"
              testId="compare-confirm-panel"
              stayOnPage
            />
          </div>
        )}
      </section>

      {loading && (
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-16" data-testid="compare-loading">
          <div className="h-64 bg-stone-100 rounded-lg animate-pulse" />
        </div>
      )}

      {!loading && cities.length === 0 && (
        <div className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="compare-empty">
          <p className="text-stone-600 mb-4">Pick cities from the <Link to="/cities" className="underline">Cities index</Link> to compare.</p>
        </div>
      )}

      {!loading && cities.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16" data-print="compare-root">
          <div className="bg-white border border-stone-200 rounded-lg overflow-x-auto" data-testid="compare-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="text-left p-4 font-normal text-xs uppercase tracking-wider text-stone-500 w-56">Metric</th>
                  {cities.map((c) => (
                    <th key={c.slug} className="text-left p-4 min-w-[200px]" data-testid={`compare-city-${c.slug}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-editorial text-lg">{c.name}</div>
                          <div className="text-xs text-stone-500 flex items-center gap-1 mt-0.5">
                            <MapPin className="h-3 w-3" /> {c.country}
                          </div>
                        </div>
                        <button
                          onClick={() => removeCity(c.slug)}
                          data-testid={`compare-remove-${c.slug}`}
                          className="text-stone-400 hover:text-red-500"
                          aria-label={`Remove ${c.name}`}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* student-friendliness */}
                <ComparisonRow label="Student-friendliness">
                  {cities.map((c) => (
                    <td key={c.slug} className="p-4 border-t border-stone-100">
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Sparkles key={i} className={`h-3 w-3 ${i < (c.student_friendliness?.score || 0) ? "fill-amber-400 text-amber-400" : "text-stone-300"}`} />
                        ))}
                      </div>
                    </td>
                  ))}
                </ComparisonRow>

                {/* cost rows with cheapest highlighted (currency-normalised) */}
                {COST_ROWS.map(([label, getter, highlight]) => {
                  const best = bestFor(getter);
                  return (
                    <ComparisonRow key={label} label={label} highlight={highlight}>
                      {cities.map((c) => {
                        const raw = getter(c);
                        const isBest = best && best.slug === c.slug && raw != null;
                        return (
                          <td
                            key={c.slug}
                            data-testid={`compare-cell-${c.slug}-${label.toLowerCase().replace(/[^a-z]+/g,'-')}`}
                            className={`p-4 border-t border-stone-100 font-mono ${
                              isBest ? "bg-emerald-50 text-emerald-800 font-semibold" : ""
                            }`}
                          >
                            {raw != null ? (
                              <>
                                <div>{currencySymbol(c.cost_of_living?.currency)}{raw.toLocaleString()}</div>
                                {c.cost_of_living?.currency !== currency && (
                                  <div className="text-[10px] text-stone-500">≈ {formatFrom(raw, c.cost_of_living?.currency)}</div>
                                )}
                              </>
                            ) : "—"}
                          </td>
                        );
                      })}
                    </ComparisonRow>
                  );
                })}

                {/* editorial rows */}
                <ComparisonRow label="Best for">
                  {cities.map((c) => (
                    <td key={c.slug} className="p-4 border-t border-stone-100 align-top">
                      <ul className="space-y-1 text-xs text-stone-700">
                        {(c.editorial?.best_for || []).map((b, i) => (
                          <li key={i}>· {b}</li>
                        ))}
                      </ul>
                    </td>
                  ))}
                </ComparisonRow>

                <ComparisonRow label="Watch out for">
                  {cities.map((c) => (
                    <td key={c.slug} className="p-4 border-t border-stone-100 align-top">
                      <ul className="space-y-1 text-xs text-amber-900">
                        {(c.editorial?.watch_out || []).map((b, i) => (
                          <li key={i}>· {b}</li>
                        ))}
                      </ul>
                    </td>
                  ))}
                </ComparisonRow>

                <ComparisonRow label="Top neighbourhoods">
                  {cities.map((c) => (
                    <td key={c.slug} className="p-4 border-t border-stone-100 align-top">
                      <div className="flex flex-wrap gap-1">
                        {(c.neighbourhoods || []).slice(0, 4).map((n, i) => (
                          <Badge key={i} variant="outline" className="text-[10px]">
                            {n.name.split("/")[0].trim()} <span className="ml-1 text-stone-500">{n.budget}</span>
                          </Badge>
                        ))}
                      </div>
                    </td>
                  ))}
                </ComparisonRow>

                <ComparisonRow label="Featured universities">
                  {cities.map((c) => (
                    <td key={c.slug} className="p-4 border-t border-stone-100 align-top">
                      <div className="flex items-center gap-1 text-xs text-stone-700">
                        <GraduationCap className="h-3 w-3" />
                        {(c.featured_universities || []).length}
                      </div>
                    </td>
                  ))}
                </ComparisonRow>

                <ComparisonRow label="Full guide">
                  {cities.map((c) => (
                    <td key={c.slug} className="p-4 border-t border-stone-100 align-top">
                      <Link
                        to={`/cities/${c.slug}`}
                        data-testid={`compare-visit-${c.slug}`}
                        className="text-xs text-[#002147] underline decoration-dotted"
                      >
                        Open {c.name} →
                      </Link>
                    </td>
                  ))}
                </ComparisonRow>
              </tbody>
            </table>
          </div>

          {/* CSV-derived research-score radars (Career / Transport / Lifestyle) */}
          <ResearchRadars cities={cities} />
        </section>
      )}
    </div>
  );
}

function ResearchRadars({ cities }) {
  const anyHas = cities.some((c) => c.research_scores);
  if (!anyHas) return null;
  return (
    <div className="mt-8" data-testid="compare-research-radars">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-editorial text-2xl">Research scores side-by-side</h2>
        <span className="text-xs text-stone-400">All axes on the same 0-10 scale</span>
      </div>
      <div className="grid gap-5 lg:grid-cols-3">
        <CompareRadar
          title="Career & industries"
          testId="compare-radar-career"
          order={CAREER_ORDER}
          labels={CAREER_SHORT}
          cities={cities}
          block="career"
          blurb="Sector strengths on a 0-10 scale, drawn from our LinkedIn / Crunchbase / hub-ranking research CSV."
        />
        <CompareRadar
          title="Transport & access"
          testId="compare-radar-transport"
          order={TRANSPORT_ORDER}
          labels={TRANSPORT_SHORT}
          cities={cities}
          block="transport"
          blurb="Public transport, air connectivity, walkability, cycling, night transport — CSV /10 scores."
        />
        <CompareRadar
          title="Lifestyle & culture"
          testId="compare-radar-lifestyle"
          order={LIFESTYLE_ORDER}
          labels={LIFESTYLE_SHORT}
          cities={cities}
          block="lifestyle"
          blurb="Thirteen lifestyle dimensions scored 0-10. A score of 7+ qualifies a city for the matching Matcher chip."
        />
      </div>
      <ClimateCompareRadar cities={cities} />
    </div>
  );
}

function ClimateCompareRadar({ cities }) {
  const withScores = cities.map((c, i) => ({
    slug: c.slug,
    name: c.name,
    scores: c.climate_scores || null,
    pal: RADAR_PALETTE[i % RADAR_PALETTE.length],
  }));
  const anyData = withScores.some((c) => c.scores);
  const data = CLIMATE_ORDER.map((k) => {
    const row = { axis: CLIMATE_SHORT[k] || k };
    withScores.forEach((c) => { row[c.name] = c.scores ? (c.scores[k] ?? 0) : 0; });
    return row;
  });
  return (
    <div className="grid gap-5 lg:grid-cols-3 mt-5" data-testid="compare-climate-radar">
      <div className="bg-white border border-stone-200 rounded-lg p-5 lg:col-span-2">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-editorial text-lg text-[#002147]">Climate profile</h3>
          <span className="text-xs text-stone-400">All axes on the same 0-100 scale</span>
        </div>
        {anyData ? (
          <>
            <div className="w-full" style={{ height: 380 }}>
              <ResponsiveContainer>
                <RadarChart data={data} outerRadius="72%" margin={{ top: 12, right: 32, bottom: 8, left: 32 }}>
                  <PolarGrid stroke="#e7e5e4" />
                  <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11, fill: "#57534e" }} tickLine={false} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 9, fill: "#a8a29e" }} tickCount={6} />
                  <Tooltip formatter={(v) => `${v}/100`} />
                  {withScores.map((c) => (
                    c.scores ? (
                      <Radar
                        key={c.slug}
                        name={c.name}
                        dataKey={c.name}
                        stroke={c.pal.stroke}
                        fill={c.pal.fill}
                        fillOpacity={c.pal.opacity}
                        strokeWidth={2}
                      />
                    ) : null
                  ))}
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {withScores.map((c) => (
                <span
                  key={c.slug}
                  className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border ${
                    c.scores ? "border-stone-200 text-stone-700" : "border-dashed border-stone-200 text-stone-400"
                  }`}
                >
                  <span className="w-2 h-2 rounded-full" style={{ background: c.pal.stroke }} />
                  {c.name}
                  {!c.scores && <span className="text-[9px] text-stone-400">data pending</span>}
                </span>
              ))}
            </div>
          </>
        ) : (
          <div className="text-xs text-stone-500 italic border border-dashed border-stone-200 rounded px-3 py-10 text-center">
            Climate data pending for these cities — check back soon.
          </div>
        )}
        <p className="text-[11px] text-stone-400 mt-3">
          Four preference axes (Warm & sunny · Mild · Four seasons · Cool) plus a derived “Overall comfort” axis (100 minus the mean of the five risk scores) from the NASA-POWER 10-year climate summaries.
        </p>
      </div>

      {/* One-line climate summaries alongside the radar for quick scan */}
      <div className="bg-white border border-stone-200 rounded-lg p-5">
        <h3 className="font-editorial text-lg text-[#002147] mb-3">In one sentence</h3>
        <div className="space-y-3">
          {withScores.map((c) => (
            <div key={c.slug} className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: c.pal.stroke }} />
              <div>
                <div className="text-xs font-semibold text-stone-800">
                  {c.name}
                  {c.scores?.annual_mean_temp_c != null && (
                    <span className="text-stone-400 font-normal font-mono ml-1.5">
                      · {c.scores.annual_mean_temp_c.toFixed(1)}°C avg
                    </span>
                  )}
                </div>
                <p className="text-xs text-stone-600 leading-snug">
                  {c.scores?.summary || <span className="italic text-stone-400">Data pending</span>}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CompareRadar({ title, testId, order, labels, cities, block, blurb }) {
  const withScores = cities.map((c, i) => ({
    slug: c.slug,
    name: c.name,
    scores: (c.research_scores || {})[block] || {},
    pal: RADAR_PALETTE[i % RADAR_PALETTE.length],
  }));
  const relevant = order.filter((k) => withScores.some((c) => c.scores[k]));
  const axes = relevant.length >= 4 ? relevant : order.slice(0, Math.min(order.length, 5));
  const data = axes.map((k) => {
    const row = { axis: labels[k] || k };
    withScores.forEach((c) => { row[c.name] = c.scores[k] ?? 0; });
    return row;
  });
  const anyData = withScores.some((c) => Object.keys(c.scores).length > 0);

  return (
    <div className="bg-white border border-stone-200 rounded-lg p-5" data-testid={testId}>
      <h3 className="font-editorial text-lg text-[#002147] mb-3">{title}</h3>
      {anyData ? (
        <>
          <div className="w-full" style={{ height: 340 }}>
            <ResponsiveContainer>
              <RadarChart data={data} outerRadius="72%" margin={{ top: 12, right: 32, bottom: 8, left: 32 }}>
                <PolarGrid stroke="#e7e5e4" />
                <PolarAngleAxis dataKey="axis" tick={{ fontSize: 10, fill: "#57534e" }} tickLine={false} />
                <PolarRadiusAxis angle={90} domain={[0, 10]} tick={{ fontSize: 9, fill: "#a8a29e" }} tickCount={6} />
                <Tooltip formatter={(v) => `${v}/10`} />
                {withScores.map((c) => (
                  Object.keys(c.scores).length > 0 ? (
                    <Radar
                      key={c.slug}
                      name={c.name}
                      dataKey={c.name}
                      stroke={c.pal.stroke}
                      fill={c.pal.fill}
                      fillOpacity={c.pal.opacity}
                      strokeWidth={2}
                    />
                  ) : null
                ))}
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {withScores.map((c) => (
              <span
                key={c.slug}
                data-testid={`${testId}-legend-${c.slug}`}
                className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full border ${
                  Object.keys(c.scores).length === 0
                    ? "border-dashed border-stone-200 text-stone-400"
                    : "border-stone-200 text-stone-700"
                }`}
              >
                <span className="w-2 h-2 rounded-full" style={{ background: c.pal.stroke }} />
                {c.name}
                {Object.keys(c.scores).length === 0 && (
                  <span className="text-[9px] text-stone-400">data pending</span>
                )}
              </span>
            ))}
          </div>
        </>
      ) : (
        <div className="text-xs text-stone-500 italic border border-dashed border-stone-200 rounded px-3 py-10 text-center">
          Research data pending for these cities — check back soon.
        </div>
      )}
      <p className="text-[11px] text-stone-400 mt-3">{blurb}</p>
    </div>
  );
}

function ComparisonRow({ label, children, highlight }) {
  return (
    <tr>
      <td className={`p-4 border-t border-stone-100 text-xs uppercase tracking-wider text-stone-600 align-top ${highlight ? "font-semibold text-stone-900" : ""}`}>
        {label}
      </td>
      {children}
    </tr>
  );
}
